import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Corporate comms & IR",
  title: "Corporate communications consistency agent",
  cardSummary:
    "Turn one approved message into on-brand, fact-consistent versions in every language — checked, flagged, and ready for sign-off.",
  heroSubhead:
    "We design, build and deploy an agent that takes one approved corporate message — an earnings statement, a press release, an internal memo — and produces the English, Bahasa Malaysia and Chinese versions that hold your brand voice, keep every material figure and claim identical, flag anything that drifts, and route to your comms or IR lead for sign-off — so your team stops reconciling versions and starts trusting them.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "For a listed or multi-region company, one off-brand translation or one inconsistent figure across languages is a real disclosure and reputation risk — this removes both while cutting the reconciliation grind.",
    complexityLabel: "Medium",
    complexityWhy:
      "An evaluator-optimizer loop — a drafter plus a consistency checker — over a single approved message; the real work is the brand-voice rubric, the figure/claim diff, and the human sign-off, not exotic infrastructure.",
    timeToLive: "~a few weeks",
    bestFit:
      "Listed companies, multi-region groups, and any brand publishing the same message in several languages",
  },
  outcomes: [
    "One approved message → on-brand versions in every language, not generic machine translation",
    "Every material figure and claim stays identical across languages — no accidental new disclosure",
    "Brand voice holds in each language, not just the source",
    "Your comms/IR lead reviews the flags and signs off — nothing publishes on its own",
  ],
  impact: {
    manualLabel: "Localising + reconciling one announcement by hand",
    manualValue: "~half a day–a day",
    agentLabel: "With the agent",
    agentValue: "~15–30 min review",
    saved:
      "≈ hours saved on every announcement that goes out in more than one language — but the bigger win is the risk it removes: a figure that reads differently in the Chinese version, or a dividend called 'special' in one language and 'final' in another, caught before release rather than after.",
    note: "Indicative; actual time depends on message length and how many languages and channels you publish to.",
  },
  businessProblem:
    "A listed group or multi-region company issues the same message — quarterly results, a price change, a policy update — and it has to go out in English, Bahasa Malaysia and Chinese, to the exchange, the press and staff, often on the same day. Today that's hand-translated or run through generic machine translation, then reconciled by whoever is free. The brand voice drifts between languages, a figure or a claim gets phrased differently in one version, and for anything price-sensitive that inconsistency is a disclosure risk. The comms team spends its time proving the versions match instead of communicating.",
  manualWorkflow: {
    steps: [
      "Approve the source message in one language",
      "Hand it to translators or run generic machine translation",
      "Read each version back for tone and accuracy",
      "Cross-check that every figure and claim matches the source",
      "Chase down and fix the drifts, under deadline",
      "Release across channels — hoping nothing slipped",
    ],
    breaks:
      "The bottleneck is reconciliation — proving that three language versions say exactly the same material thing, in the right voice, before a hard deadline.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as an evaluator-optimizer loop: the agent drafts each localized version against your brand-voice guide, then a checker compares it back to the approved source — every figure, every material claim, the tone — and the drafter revises until it passes. Anything it can't resolve is flagged for a human, and nothing publishes without sign-off.",
    steps: [
      "Take the approved source + your brand guide + key facts",
      "Draft the localized version in each language",
      "Check it back against the source — figures, claims, tone",
      "Revise until every material point matches and the voice holds",
      "Route to your comms/IR lead with drift flags for sign-off",
    ],
    inLabel: "An approved message + your brand guide",
    outLabel: "Sign-off-ready versions in every language",
    loopAround: {
      index: 2,
      label:
        "Re-drafts until every figure, claim and the brand voice pass the consistency check — otherwise it flags a human.",
    },
    humanStep: "Your comms/IR lead reviews the flags and signs off",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Source (English, approved)",
    inMessage:
      "The Group recorded revenue of RM 1.42 billion for FY2025, up 11% year-on-year, and declared a final dividend of 3.5 sen per share.",
    outLabel: "Localised — checked & flagged",
    outLines: [
      "BM + Chinese drafted in your brand voice",
      "✓ RM 1.42b · +11% YoY · 3.5 sen — identical across all three",
      "⚠ BM draft called the dividend 'istimewa' (special) — source says 'final' · flagged for a human",
    ],
    outResult: "3 language versions · every figure matched · 1 wording flag",
    note: "Nothing publishes until your IR lead signs off · every figure checked back against the approved source · ~RM 2–7 in AI cost.",
  },
  workflowDetail: [
    {
      title: "Take the approved source",
      detail:
        "The agent starts only from the message you've already approved — plus your brand-voice guide, your approved glossary, and the key facts. It never invents the source; it localizes what's signed off.",
    },
    {
      title: "Draft in each language",
      detail:
        "It writes the English, Bahasa Malaysia and Chinese versions in your house voice — not a literal word-for-word translation, but the same message said the way your brand says it in that language.",
    },
    {
      title: "Check every figure and claim back to source",
      detail:
        "A separate checker extracts every number, date and material claim from the source and from each draft, and diffs them. A figure that reads differently, a claim that's softened or added, a dividend labelled differently — all surfaced, not buried.",
    },
    {
      title: "Revise until it passes",
      detail:
        "Where the check fails, the drafter revises and re-checks. It loops until every material point matches the source and the brand voice holds — or, if it can't resolve something, it stops and flags it.",
    },
    {
      title: "Route for human sign-off",
      detail:
        "It hands your comms or IR lead the set of versions with any drift flags called out — so review is minutes on the exceptions, not a full re-read of every language.",
    },
    {
      title: "Release, logged",
      detail:
        "Once signed off, the versions go out through your channels — and every draft, check and flag is logged, so any released version can be reconstructed and audited later.",
    },
  ],
  howWeBuild:
    "Because it drafts against your brand-voice guide and checks every version back to the approved source, it can't quietly introduce a new claim or a different number — and because a human signs off on the flags, the agent never releases anything on its own. Every version, every check and every flag is logged for audit.",
  agentLoop: [
    "the approved source + your brand guide & glossary",
    "draft each language version (MCP tools)",
    "check every figure, claim & the voice back to source",
  ],
  integrations: [
    "Your brand-voice / tone-of-voice guide + approved glossary",
    "Your CMS, IR portal or newswire (exchange filing, press)",
    "Internal channels (email, intranet, Slack / Teams)",
    "Optional: your translation memory",
  ],
  architecture: [
    "Pattern: an evaluator-optimizer loop on the Claude Agent SDK — a drafter and a checker, each tool exposed via an in-process MCP server.",
    "Context: your brand-voice guide, approved glossary and the source message are loaded each turn and prompt-cached, so drafts stay on-voice and on-terminology.",
    "Tools: read-only source + glossary lookups, a per-language drafter, and a consistency-checker that extracts every figure and material claim from source and draft and diffs them.",
    "Guardrails: it can't mark a version 'ready' until every figure and material claim matches the source; it never publishes — it routes to a human sign-off queue; least-privilege access to your CMS/newswire.",
    "Untrusted input: any external copy pulled in (analyst text, prior coverage) is treated as untrusted and prompt-injection resistant; personal data in staff comms handled PDPA/PDPC-aligned.",
    "Recovery: on an unresolved drift it stops and flags for a human — never a silent mis-statement or a version released unchecked.",
    "Observability: every draft, check and flag is logged and traceable, so any released version can be reconstructed and audited.",
    "Eval harness: a golden set of your past announcements — including the tricky, price-sensitive ones — regression-tested on every change before it touches a live release.",
  ],
  modelChoice: {
    approach: "Two roles, one loop",
    rationale:
      "Drafting on-voice and checking for consistency are different jobs, so we split them into a drafter and a checker — but it's still one tight loop over a single approved message, not a sprawling multi-agent system.",
    costNote:
      "We reserve heavy multi-agent designs (≈15× the tokens) for genuinely parallel work — a single announcement is a focused draft-and-check loop.",
  },
  models: [
    {
      task: "Draft the localized version on-voice",
      model: "Claude Sonnet",
      why: "Reliable, on-brand writing across languages — not literal translation.",
    },
    {
      task: "Check figures & claims back to source",
      model: "Claude Sonnet",
      why: "Careful extraction and diffing of every material fact between source and draft.",
    },
    {
      task: "Bulk glossary / terminology matching",
      model: "Claude Haiku",
      why: "Cheap and fast for keeping approved terms consistent across versions.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per announcement",
        value: "~RM 2–7",
        note: "a few languages with a draft-and-check loop, at Claude token rates",
      },
      {
        label: "AI usage — at 30 announcements/month",
        value: "~RM 50–220 / month",
        note: "scales with message length and number of languages",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "connecting your brand guide, CMS/newswire and channels is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to a single avoided mis-disclosure or off-brand release",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on message length and how many languages you publish. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  evaluation: [
    "Deterministic checks first — every figure in the source appears, identical, in each version",
    "Self-verification — the checker diffs each draft's material claims against the source before it's marked ready",
    "Business KPIs — reconciliation time saved, drifts caught before release, and zero inconsistent disclosures",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of your human review baseline.",
    baseline:
      "First we measure your current process on a sample of past announcements — how long localization and reconciliation take, and how many drifts (a figure, a claim, a tone slip) reach a human late. That baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did every version say the same material thing?",
        metrics: [
          {
            name: "Material-claim consistency",
            measures:
              "Every figure and material claim in a version matches the approved source, on held-out announcements",
            target: "100%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the voice and structure right?",
        metrics: [
          {
            name: "Brand-voice adherence",
            measures: "Each version scores against your tone-of-voice rubric",
            target: "monitored",
          },
          {
            name: "No omitted disclosure",
            measures: "Every required section and caveat present in each language",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual conversions correct?",
        metrics: [
          {
            name: "Figure accuracy",
            measures: "Every number, date and percentage identical to the source",
            target: "100%",
          },
          {
            name: "Terminology consistency",
            measures: "Approved glossary terms used the same way across versions",
            target: "≥ 98%",
          },
          {
            name: "No invented claims",
            measures: "Never adds a statement that isn't in the approved source",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent doesn't mark a version ready for sign-off until it matches the source on every figure and material claim and clears these GPA targets on a golden set of your past announcements. A human always signs off before release.",
  },
  delivery:
    "We start with a proof-of-concept on your real past announcements, pilot it on one message type — say internal comms — with your comms lead reviewing every version, then extend to IR and press. A focused rollout is a matter of weeks.",
  ctaTitle: "Publishing the same message in three languages?",
  ctaDescription:
    "Tell us what you publish and in which languages, and we'll tell you honestly whether a consistency agent is worth building — and what it would take.",
  faqs: [
    {
      question: "Does it replace our translators or comms team?",
      answer:
        "No. It does the first draft and the consistency check, and it flags what needs a human eye — your team reviews and signs off. It removes the reconciliation grind, not the judgment.",
    },
    {
      question: "How does it keep our brand voice?",
      answer:
        "We load your tone-of-voice guide and a set of past approved messages, and the agent both drafts and self-checks against them. Voice is scored on every version before a human sees it — so it reads like your brand in each language, not like a translation.",
    },
    {
      question: "Is it safe for price-sensitive or disclosure material?",
      answer:
        "It's built for exactly that: every figure and material claim is checked back to the approved source, nothing publishes on its own, and every version is logged for audit. It supports your disclosure process as a control — it doesn't replace your advisers' or board's judgment on what to disclose.",
    },
    {
      question: "Which languages does it handle?",
      answer:
        "English, Bahasa Malaysia and Chinese out of the box, and we can add others. It also holds your approved glossary, so regulated and brand terms are translated consistently every time.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Komunikasi korporat & IR",
  title: "Ejen konsistensi komunikasi korporat",
  cardSummary:
    "Tukar satu mesej yang diluluskan kepada versi yang menepati jenama dan konsisten fakta dalam setiap bahasa — disemak, ditanda, dan sedia untuk kelulusan.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen yang mengambil satu mesej korporat yang diluluskan — penyata pendapatan, siaran akhbar, memo dalaman — dan menghasilkan versi Inggeris, Bahasa Malaysia dan Cina yang mengekalkan suara jenama anda, memastikan setiap angka dan dakwaan penting kekal serupa, menanda apa-apa yang menyimpang, dan menghalakannya kepada ketua komunikasi atau IR anda untuk kelulusan — supaya pasukan anda berhenti menyelaras versi dan mula mempercayainya.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Bagi syarikat tersenarai atau berbilang rantau, satu terjemahan tersasar jenama atau satu angka tak konsisten merentas bahasa ialah risiko pendedahan dan reputasi yang nyata — ini menghapuskan kedua-duanya sambil mengurangkan kerja penyelarasan.",
    complexityLabel: "Sederhana",
    complexityWhy:
      "Gelung evaluator-optimizer — pendraf serta penyemak konsistensi — atas satu mesej yang diluluskan; kerja sebenar ialah rubrik suara jenama, perbandingan angka/dakwaan, dan kelulusan manusia — bukan infrastruktur eksotik.",
    timeToLive: "~beberapa minggu",
    bestFit:
      "Syarikat tersenarai, kumpulan berbilang rantau, dan mana-mana jenama yang menerbitkan mesej sama dalam beberapa bahasa",
  },
  outcomes: [
    "Satu mesej diluluskan → versi menepati jenama dalam setiap bahasa, bukan terjemahan mesin generik",
    "Setiap angka dan dakwaan penting kekal serupa merentas bahasa — tiada pendedahan baharu tak sengaja",
    "Suara jenama kekal dalam setiap bahasa, bukan hanya sumber",
    "Ketua komunikasi/IR anda menyemak tanda dan meluluskan — tiada apa yang terbit sendiri",
  ],
  impact: {
    manualLabel: "Setempatkan + selaras satu pengumuman secara manual",
    manualValue: "~separuh hari–sehari",
    agentLabel: "Dengan ejen",
    agentValue: "~15–30 min semak",
    saved:
      "≈ berjam-jam dijimatkan pada setiap pengumuman yang keluar dalam lebih satu bahasa — tetapi kemenangan lebih besar ialah risiko yang dihapuskan: angka yang berbeza bacaan dalam versi Cina, atau dividen dipanggil 'istimewa' dalam satu bahasa dan 'akhir' dalam bahasa lain, ditangkap sebelum terbit dan bukan selepas.",
    note: "Anggaran; masa sebenar bergantung pada panjang mesej dan berapa bahasa serta saluran yang anda terbitkan.",
  },
  businessProblem:
    "Sebuah kumpulan tersenarai atau syarikat berbilang rantau menerbitkan mesej yang sama — keputusan suku tahun, perubahan harga, kemas kini dasar — dan ia perlu keluar dalam Bahasa Inggeris, Bahasa Malaysia dan Cina, kepada bursa, akhbar dan kakitangan, selalunya pada hari yang sama. Hari ini ia diterjemah tangan atau melalui terjemahan mesin generik, kemudian diselaras oleh sesiapa yang lapang. Suara jenama menyimpang antara bahasa, satu angka atau dakwaan diungkap berbeza dalam satu versi, dan bagi apa-apa yang sensitif harga, ketidakkonsistenan itu ialah risiko pendedahan. Pasukan komunikasi menghabiskan masa membuktikan versi sepadan dan bukannya berkomunikasi.",
  manualWorkflow: {
    steps: [
      "Luluskan mesej sumber dalam satu bahasa",
      "Serahkan kepada penterjemah atau jalankan terjemahan mesin generik",
      "Baca semula setiap versi untuk nada dan ketepatan",
      "Semak silang bahawa setiap angka dan dakwaan sepadan dengan sumber",
      "Kesan dan betulkan penyimpangan, dalam tempoh masa",
      "Terbitkan merentas saluran — berharap tiada yang terlepas",
    ],
    breaks:
      "Halangan ialah penyelarasan — membuktikan bahawa tiga versi bahasa menyatakan perkara penting yang sama, dalam suara yang betul, sebelum tarikh akhir yang ketat.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai gelung evaluator-optimizer: ejen mendraf setiap versi setempat berdasarkan panduan suara jenama anda, kemudian penyemak membandingkannya semula dengan sumber yang diluluskan — setiap angka, setiap dakwaan penting, nadanya — dan pendraf menyemak semula sehingga ia lulus. Apa yang tidak dapat diselesaikan ditanda untuk manusia, dan tiada apa yang terbit tanpa kelulusan.",
    steps: [
      "Ambil sumber diluluskan + panduan jenama + fakta utama",
      "Draf versi setempat dalam setiap bahasa",
      "Semak semula dengan sumber — angka, dakwaan, nada",
      "Semak semula sehingga setiap perkara penting sepadan dan suara kekal",
      "Halakan kepada ketua komunikasi/IR dengan tanda penyimpangan untuk kelulusan",
    ],
    inLabel: "Mesej diluluskan + panduan jenama anda",
    outLabel: "Versi sedia-kelulusan dalam setiap bahasa",
    loopAround: {
      index: 2,
      label:
        "Mendraf semula sehingga setiap angka, dakwaan dan suara jenama lulus semakan konsistensi — jika tidak, ia menanda manusia.",
    },
    humanStep: "Ketua komunikasi/IR anda menyemak tanda dan meluluskan",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Sumber (Inggeris, diluluskan)",
    inMessage:
      "The Group recorded revenue of RM 1.42 billion for FY2025, up 11% year-on-year, and declared a final dividend of 3.5 sen per share.",
    outLabel: "Disetempatkan — disemak & ditanda",
    outLines: [
      "BM + Cina didraf dalam suara jenama anda",
      "✓ RM 1.42b · +11% YoY · 3.5 sen — serupa dalam ketiga-tiganya",
      "⚠ Draf BM memanggil dividen 'istimewa' — sumber kata 'akhir' · ditanda untuk manusia",
    ],
    outResult: "3 versi bahasa · setiap angka sepadan · 1 tanda perkataan",
    note: "Tiada apa yang terbit sehingga ketua IR anda meluluskan · setiap angka disemak semula dengan sumber diluluskan · ~RM 2–7 kos AI.",
  },
  workflowDetail: [
    {
      title: "Ambil sumber yang diluluskan",
      detail:
        "Ejen bermula hanya daripada mesej yang telah anda luluskan — bersama panduan suara jenama, glosari diluluskan, dan fakta utama. Ia tidak pernah mereka sumber; ia menyetempatkan apa yang telah diluluskan.",
    },
    {
      title: "Draf dalam setiap bahasa",
      detail:
        "Ia menulis versi Inggeris, Bahasa Malaysia dan Cina dalam suara rumah anda — bukan terjemahan harfiah perkataan demi perkataan, tetapi mesej yang sama disebut cara jenama anda menyebutnya dalam bahasa itu.",
    },
    {
      title: "Semak setiap angka dan dakwaan dengan sumber",
      detail:
        "Penyemak berasingan mengekstrak setiap nombor, tarikh dan dakwaan penting daripada sumber dan setiap draf, lalu membandingkannya. Angka yang berbeza bacaan, dakwaan yang dilembutkan atau ditambah, dividen dilabel berbeza — semua ditonjolkan, bukan disembunyikan.",
    },
    {
      title: "Semak semula sehingga lulus",
      detail:
        "Di mana semakan gagal, pendraf menyemak semula dan menyemak sekali lagi. Ia berulang sehingga setiap perkara penting sepadan dengan sumber dan suara jenama kekal — atau, jika tidak dapat menyelesaikan sesuatu, ia berhenti dan menandanya.",
    },
    {
      title: "Halakan untuk kelulusan manusia",
      detail:
        "Ia menyerahkan kepada ketua komunikasi atau IR anda set versi dengan sebarang tanda penyimpangan ditonjolkan — jadi semakan hanya beberapa minit pada pengecualian, bukan bacaan semula penuh setiap bahasa.",
    },
    {
      title: "Terbit, berlog",
      detail:
        "Setelah diluluskan, versi keluar melalui saluran anda — dan setiap draf, semakan dan tanda dilog, jadi mana-mana versi yang diterbitkan boleh dibina semula dan diaudit kemudian.",
    },
  ],
  howWeBuild:
    "Kerana ia mendraf berdasarkan panduan suara jenama anda dan menyemak setiap versi semula dengan sumber yang diluluskan, ia tidak boleh secara senyap memperkenalkan dakwaan baharu atau nombor berbeza — dan kerana manusia meluluskan tanda, ejen tidak pernah menerbitkan apa-apa sendiri. Setiap versi, setiap semakan dan setiap tanda dilog untuk audit.",
  agentLoop: [
    "sumber diluluskan + panduan jenama & glosari anda",
    "draf setiap versi bahasa (alat MCP)",
    "semak setiap angka, dakwaan & suara semula dengan sumber",
  ],
  integrations: [
    "Panduan suara jenama anda + glosari diluluskan",
    "CMS, portal IR atau newswire anda (failan bursa, akhbar)",
    "Saluran dalaman (e-mel, intranet, Slack / Teams)",
    "Pilihan: memori terjemahan anda",
  ],
  architecture: [
    "Corak: gelung evaluator-optimizer atas Claude Agent SDK — pendraf dan penyemak, setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: panduan suara jenama, glosari diluluskan dan mesej sumber anda dimuatkan setiap giliran dan di-cache-prompt, jadi draf kekal pada-suara dan pada-terminologi.",
    "Alat: semakan sumber + glosari baca-sahaja, pendraf setiap bahasa, dan penyemak konsistensi yang mengekstrak setiap angka dan dakwaan penting daripada sumber dan draf lalu membandingkannya.",
    "Pagar keselamatan: ia tidak boleh menanda versi 'sedia' sehingga setiap angka dan dakwaan penting sepadan dengan sumber; ia tidak pernah menerbitkan — ia menghalakan ke baris gilir kelulusan manusia; akses keistimewaan-minimum ke CMS/newswire anda.",
    "Input tidak dipercayai: mana-mana salinan luaran yang dimasukkan (teks penganalisis, liputan terdahulu) dianggap tidak dipercayai dan tahan suntikan-prompt; data peribadi dalam komunikasi kakitangan dikendalikan selaras PDPA/PDPC.",
    "Pemulihan: apabila penyimpangan tidak dapat diselesaikan ia berhenti dan menanda untuk manusia — tiada salah nyata senyap atau versi diterbit tanpa semakan.",
    "Kebolehlihatan: setiap draf, semakan dan tanda dilog dan boleh dijejak, jadi mana-mana versi yang diterbitkan boleh dibina semula dan diaudit.",
    "Rangka penilaian: set emas pengumuman lepas anda — termasuk yang rumit dan sensitif harga — diuji regresi pada setiap perubahan sebelum menyentuh terbitan langsung.",
  ],
  modelChoice: {
    approach: "Dua peranan, satu gelung",
    rationale:
      "Mendraf pada-suara dan menyemak konsistensi ialah kerja berbeza, jadi kami memisahkannya kepada pendraf dan penyemak — tetapi ia masih satu gelung ketat atas satu mesej yang diluluskan, bukan sistem berbilang-ejen yang berselerak.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen berat (≈15× token) untuk kerja yang benar-benar selari — satu pengumuman ialah gelung draf-dan-semak yang fokus.",
  },
  models: [
    {
      task: "Draf versi setempat pada-suara",
      model: "Claude Sonnet",
      why: "Penulisan boleh dipercayai dan menepati jenama merentas bahasa — bukan terjemahan harfiah.",
    },
    {
      task: "Semak angka & dakwaan semula dengan sumber",
      model: "Claude Sonnet",
      why: "Pengekstrakan dan perbandingan teliti setiap fakta penting antara sumber dan draf.",
    },
    {
      task: "Padanan glosari / terminologi pukal",
      model: "Claude Haiku",
      why: "Murah dan pantas untuk mengekalkan istilah diluluskan konsisten merentas versi.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap pengumuman",
        value: "~RM 2–7",
        note: "beberapa bahasa dengan gelung draf-dan-semak, pada kadar token Claude",
      },
      {
        label: "Penggunaan AI — pada 30 pengumuman/bulan",
        value: "~RM 50–220 / bulan",
        note: "berskala mengikut panjang mesej dan bilangan bahasa",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "menyambung panduan jenama, CMS/newswire dan saluran anda adalah beberapa minggu; sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding satu salah-dedah atau terbitan tersasar jenama yang dielakkan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada panjang mesej dan berapa bahasa yang anda terbitkan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  evaluation: [
    "Semakan deterministik dahulu — setiap angka dalam sumber muncul, serupa, dalam setiap versi",
    "Pengesahan sendiri — penyemak membandingkan dakwaan penting setiap draf dengan sumber sebelum ditanda sedia",
    "KPI perniagaan — masa penyelarasan dijimatkan, penyimpangan ditangkap sebelum terbit, dan sifar pendedahan tak konsisten",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar semakan manusia anda.",
    baseline:
      "Mula-mula kami mengukur proses semasa anda pada sampel pengumuman lepas — berapa lama penyetempatan dan penyelarasan mengambil masa, dan berapa penyimpangan (angka, dakwaan, gelinciran nada) sampai kepada manusia lewat. Garis dasar itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah setiap versi menyatakan perkara penting yang sama?",
        metrics: [
          {
            name: "Konsistensi dakwaan penting",
            measures:
              "Setiap angka dan dakwaan penting dalam versi sepadan dengan sumber diluluskan, pada pengumuman terpencil",
            target: "100%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah suara dan struktur betul?",
        metrics: [
          {
            name: "Pematuhan suara jenama",
            measures: "Setiap versi dinilai terhadap rubrik nada-suara anda",
            target: "dipantau",
          },
          {
            name: "Tiada pendedahan tertinggal",
            measures: "Setiap seksyen dan kaveat yang diperlukan hadir dalam setiap bahasa",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap penukaran betul?",
        metrics: [
          {
            name: "Ketepatan angka",
            measures: "Setiap nombor, tarikh dan peratus serupa dengan sumber",
            target: "100%",
          },
          {
            name: "Konsistensi terminologi",
            measures: "Istilah glosari diluluskan digunakan cara sama merentas versi",
            target: "≥ 98%",
          },
          {
            name: "Tiada dakwaan direka",
            measures: "Tidak pernah menambah kenyataan yang tiada dalam sumber diluluskan",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menanda versi sedia-kelulusan sehingga ia sepadan dengan sumber pada setiap angka dan dakwaan penting dan mencapai sasaran GPA ini pada set emas pengumuman lepas anda. Manusia sentiasa meluluskan sebelum terbit.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas pengumuman lepas sebenar anda, menguji pada satu jenis mesej — katakan komunikasi dalaman — dengan ketua komunikasi anda menyemak setiap versi, kemudian meluaskan ke IR dan akhbar. Pelancaran yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Menerbitkan mesej sama dalam tiga bahasa?",
  ctaDescription:
    "Beritahu kami apa yang anda terbitkan dan dalam bahasa apa, dan kami akan beritahu dengan jujur sama ada ejen konsistensi berbaloi dibina — dan apa yang diperlukan.",
  faqs: [
    {
      question: "Adakah ia menggantikan penterjemah atau pasukan komunikasi kami?",
      answer:
        "Tidak. Ia membuat draf pertama dan semakan konsistensi, dan menanda apa yang perlukan mata manusia — pasukan anda menyemak dan meluluskan. Ia menghapuskan kerja penyelarasan, bukan pertimbangan.",
    },
    {
      question: "Bagaimana ia mengekalkan suara jenama kami?",
      answer:
        "Kami memuatkan panduan nada-suara anda dan set mesej lepas yang diluluskan, dan ejen mendraf serta menyemak sendiri berdasarkannya. Suara dinilai pada setiap versi sebelum manusia melihatnya — jadi ia berbunyi seperti jenama anda dalam setiap bahasa, bukan seperti terjemahan.",
    },
    {
      question: "Adakah ia selamat untuk bahan sensitif harga atau pendedahan?",
      answer:
        "Ia dibina khusus untuk itu: setiap angka dan dakwaan penting disemak semula dengan sumber diluluskan, tiada apa yang terbit sendiri, dan setiap versi dilog untuk audit. Ia menyokong proses pendedahan anda sebagai kawalan — ia tidak menggantikan pertimbangan penasihat atau lembaga anda tentang apa yang perlu didedahkan.",
    },
    {
      question: "Bahasa apa yang ia kendalikan?",
      answer:
        "Bahasa Inggeris, Bahasa Malaysia dan Cina secara lalai, dan kami boleh menambah yang lain. Ia juga memegang glosari diluluskan anda, jadi istilah dikawal selia dan jenama diterjemah konsisten setiap kali.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "企业传播与投资者关系",
  title: "企业传播一致性智能体",
  cardSummary:
    "把一条已批准的信息转化为在每种语言中都符合品牌、事实一致的版本——经过核对、标记，随时可供签发。",
  heroSubhead:
    "我们设计、构建并部署一个智能体，它接收一条已批准的企业信息——业绩公告、新闻稿、内部通知——并生成英文、马来文和中文版本，保持你的品牌口吻，让每一个关键数字与表述保持一致，标记任何偏差，并转交你的传播或投资者关系负责人签发——让你的团队不再核对版本，而是开始信任它们。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "对上市或跨区域公司而言，一处偏离品牌的翻译、或各语言间一个不一致的数字，都是实实在在的披露与声誉风险——这能在削减核对苦工的同时消除两者。",
    complexityLabel: "中等",
    complexityWhy:
      "一个 evaluator-optimizer 循环——一个起草者加一个一致性核查者——作用于一条已批准的信息；真正的工作是品牌口吻评分、数字/表述比对，以及人工签发，而非奇特的基础设施。",
    timeToLive: "约数周",
    bestFit: "上市公司、跨区域集团，以及任何用多种语言发布同一信息的品牌",
  },
  outcomes: [
    "一条已批准的信息 → 每种语言都符合品牌的版本，而非通用机器翻译",
    "每个关键数字与表述在各语言间保持一致——不会意外产生新的披露",
    "品牌口吻在每种语言中都得以保持，而不仅是源语言",
    "你的传播/投资者关系负责人审阅标记并签发——没有任何内容会自行发布",
  ],
  impact: {
    manualLabel: "人工本地化 + 核对一则公告",
    manualValue: "约半天–一天",
    agentLabel: "使用智能体",
    agentValue: "约 15–30 分钟审阅",
    saved:
      "≈ 每一则以多种语言发出的公告都能节省数小时——但更大的收获是它消除的风险：中文版中读起来不同的数字，或在一种语言里叫“特别”股息、另一种语言里叫“末期”股息，都能在发布前而非发布后被抓出。",
    note: "为估算；实际时间取决于信息长度以及你发布的语言与渠道数量。",
  },
  businessProblem:
    "一家上市集团或跨区域公司发布同一条信息——季度业绩、价格调整、政策更新——它必须以英文、马来文和中文发出，面向交易所、媒体和员工，往往在同一天。如今这要么人工翻译、要么走通用机器翻译，然后由谁有空谁来核对。品牌口吻在语言间漂移，某个数字或表述在某一版本里被换了说法，对任何价格敏感的内容而言，这种不一致就是披露风险。传播团队把时间花在证明版本一致，而不是传播本身。",
  manualWorkflow: {
    steps: [
      "以一种语言批准源信息",
      "交给译者或走通用机器翻译",
      "逐版回读，核对口吻与准确性",
      "交叉核对每个数字与表述是否与源一致",
      "在截止期限内追查并修正偏差",
      "跨渠道发布——但愿没有遗漏",
    ],
    breaks:
      "瓶颈在于核对——在严格的截止期限前，证明三个语言版本以正确的口吻说出完全相同的关键内容。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 evaluator-optimizer 循环构建：智能体依据你的品牌口吻指南起草每个本地化版本，然后核查者将其与已批准的源逐一比对——每个数字、每处关键表述、口吻——起草者不断修订直至通过。无法解决的会标记给人工，未经签发不会发布任何内容。",
    steps: [
      "取用已批准的源 + 你的品牌指南 + 关键事实",
      "以每种语言起草本地化版本",
      "与源逐一核对——数字、表述、口吻",
      "反复修订，直到每处关键内容一致且口吻保持",
      "带着偏差标记转交你的传播/投资者关系负责人签发",
    ],
    inLabel: "一条已批准的信息 + 你的品牌指南",
    outLabel: "每种语言都可供签发的版本",
    loopAround: {
      index: 2,
      label: "反复起草，直到每个数字、表述与品牌口吻都通过一致性核查——否则标记人工。",
    },
    humanStep: "你的传播/投资者关系负责人审阅标记并签发",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "源（英文，已批准）",
    inMessage:
      "The Group recorded revenue of RM 1.42 billion for FY2025, up 11% year-on-year, and declared a final dividend of 3.5 sen per share.",
    outLabel: "已本地化——已核对与标记",
    outLines: [
      "马来文 + 中文以你的品牌口吻起草",
      "✓ RM 1.42b · +11% 同比 · 3.5 sen — 三个版本完全一致",
      "⚠ 马来文草稿把股息写成“istimewa”（特别）— 源为“末期”· 已标记给人工",
    ],
    outResult: "3 个语言版本 · 每个数字一致 · 1 处措辞标记",
    note: "在你的投资者关系负责人签发前不会发布任何内容 · 每个数字都与已批准的源核对 · AI 成本约 RM 2–7。",
  },
  workflowDetail: [
    {
      title: "取用已批准的源",
      detail:
        "智能体只从你已经批准的信息出发——外加你的品牌口吻指南、已批准术语表和关键事实。它绝不臆造源；它只本地化已签发的内容。",
    },
    {
      title: "以每种语言起草",
      detail:
        "它以你的“家族口吻”撰写英文、马来文和中文版本——不是逐字直译，而是用你的品牌在那种语言里的说法，说出同一条信息。",
    },
    {
      title: "把每个数字与表述与源核对",
      detail:
        "一个独立的核查者从源和每份草稿中抽取每个数字、日期和关键表述并进行比对。读起来不同的数字、被弱化或新增的表述、被标注不同的股息——全部被凸显，而非被埋没。",
    },
    {
      title: "反复修订直至通过",
      detail:
        "核查未通过之处，起草者修订并重新核查。它循环直到每处关键内容都与源一致且品牌口吻保持——或者，若某处无法解决，它便停下并标记出来。",
    },
    {
      title: "转交人工签发",
      detail:
        "它把成套版本连同任何偏差标记一并交给你的传播或投资者关系负责人——因此审阅只需在例外之处花几分钟，而非逐语言通读。",
    },
    {
      title: "发布，并留痕",
      detail:
        "一经签发，各版本便通过你的渠道发出——而每份草稿、每次核查与每处标记都会留痕，因此任何已发布的版本日后都能被还原与审计。",
    },
  ],
  howWeBuild:
    "因为它依据你的品牌口吻指南起草，并把每个版本与已批准的源核对，它无法悄悄引入新的表述或不同的数字——又因为由人工对标记签发，智能体绝不自行发布任何内容。每个版本、每次核查与每处标记都会留痕以供审计。",
  agentLoop: [
    "已批准的源 + 你的品牌指南与术语表",
    "起草每个语言版本（MCP 工具）",
    "把每个数字、表述与口吻与源核对",
  ],
  integrations: [
    "你的品牌口吻指南 + 已批准术语表",
    "你的 CMS、投资者关系门户或新闻线（交易所披露、媒体）",
    "内部渠道（邮件、内网、Slack / Teams）",
    "可选：你的翻译记忆库",
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的 evaluator-optimizer 循环——一个起草者与一个核查者，每个工具通过进程内 MCP 服务器提供。",
    "上下文：你的品牌口吻指南、已批准术语表与源信息每轮都会载入并做提示缓存，因此草稿始终贴合口吻与术语。",
    "工具：只读的源 + 术语表查询、每种语言的起草器，以及一个从源与草稿中抽取每个数字和关键表述并进行比对的一致性核查器。",
    "防护机制：在每个数字与关键表述都与源一致前，它无法把版本标记为“就绪”；它绝不发布——而是转入人工签发队列；对你的 CMS/新闻线采用最小权限访问。",
    "不可信输入：任何引入的外部文字（分析师文本、过往报道）都被视为不可信且可抵御提示注入；员工传播中的个人数据按 PDPA/PDPC 处理。",
    "恢复：遇到无法解决的偏差，它停下并标记给人工——绝不静默地误述或发布未经核查的版本。",
    "可观测性：每份草稿、每次核查与每处标记都会留痕且可追溯，任何已发布的版本都能被还原与审计。",
    "评估框架：一组你过往公告的黄金集——含棘手的、价格敏感的那些——每次改动在触及实时发布前都做回归测试。",
  ],
  modelChoice: {
    approach: "两个角色，一个循环",
    rationale:
      "贴合口吻地起草与核查一致性是两种不同的活，因此我们把它们拆成起草者与核查者——但它仍是围绕一条已批准信息的一个紧凑循环，而非庞杂的多智能体系统。",
    costNote:
      "我们把繁重的多智能体设计（约 15 倍 token）留给真正并行的工作——一则公告是一个专注的起草-核查循环。",
  },
  models: [
    {
      task: "贴合口吻地起草本地化版本",
      model: "Claude Sonnet",
      why: "跨语言可靠、符合品牌的写作——而非直译。",
    },
    {
      task: "把数字与表述与源核对",
      model: "Claude Sonnet",
      why: "在源与草稿之间对每个关键事实做细致的抽取与比对。",
    },
    {
      task: "批量术语 / 词汇匹配",
      model: "Claude Haiku",
      why: "在各版本间保持已批准术语一致，既便宜又快。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每则公告",
        value: "约 RM 2–7",
        note: "数种语言加一个起草-核查循环，按 Claude 的 token 费率",
      },
      {
        label: "AI 用量——每月 30 则公告",
        value: "约 RM 50–220 / 月",
        note: "随信息长度与语言数量而变",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "接入你的品牌指南、CMS/新闻线与渠道约需数周；简短的需求梳理后给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比避免的一次误披露或偏离品牌的发布，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于信息长度与你发布的语言数量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  evaluation: [
    "先做确定性检查——源中的每个数字都以相同形式出现在每个版本里",
    "自我验证——核查者在版本被标记为就绪前，把每份草稿的关键表述与源比对",
    "业务 KPI——节省的核对时间、发布前抓出的偏差，以及零不一致披露",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合你的人工审阅基线来评估。",
    baseline:
      "我们先在一组过往公告样本上测量你当前的流程——本地化与核对各需多久，以及有多少偏差（一个数字、一处表述、一处口吻走样）迟迟才到人工手里。这个基线是衡量每个智能体指标的基准，因此提升是可证明的。",
    dimensions: [
      {
        dim: "Goal",
        desc: "各版本是否说出了相同的关键内容？",
        metrics: [
          {
            name: "关键表述一致性",
            measures: "在留出的公告上，版本中的每个数字与关键表述都与已批准的源一致",
            target: "100%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "口吻与结构是否正确？",
        metrics: [
          {
            name: "品牌口吻符合度",
            measures: "每个版本对照你的口吻评分表打分",
            target: "持续监控",
          },
          {
            name: "无遗漏披露",
            measures: "每种语言中所有必需的段落与提示语均齐备",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各处转换是否正确？",
        metrics: [
          {
            name: "数字准确率",
            measures: "每个数字、日期与百分比都与源一致",
            target: "100%",
          },
          {
            name: "术语一致性",
            measures: "已批准术语表中的词在各版本间用法一致",
            target: "≥ 98%",
          },
          {
            name: "不凭空新增表述",
            measures: "绝不添加已批准源中没有的陈述",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于你过往公告的黄金集上、每个数字与关键表述都与源一致并达到这些 GPA 目标之前，它不会把版本标记为可供签发。发布前始终由人工签发。",
  },
  delivery:
    "我们先在你真实的过往公告上做概念验证，在一种信息类型上试运行——比如内部传播——由你的传播负责人审阅每个版本，然后扩展到投资者关系与媒体。聚焦的落地只需数周。",
  ctaTitle: "在用三种语言发布同一条信息？",
  ctaDescription:
    "告诉我们你发布什么、用哪些语言，我们会诚实告诉你一致性智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "它会取代我们的译者或传播团队吗？",
      answer:
        "不会。它做初稿和一致性核查，并标记需要人工把关之处——由你的团队审阅并签发。它消除的是核对苦工，而非判断。",
    },
    {
      question: "它如何保持我们的品牌口吻？",
      answer:
        "我们载入你的口吻指南和一组过往已批准的信息，智能体据此既起草又自我核查。口吻会在人工看到之前对每个版本打分——因此它在每种语言里读起来都像你的品牌，而不像翻译。",
    },
    {
      question: "对价格敏感或披露性内容安全吗？",
      answer:
        "它正是为此而建：每个数字与关键表述都与已批准的源核对，没有任何内容会自行发布，且每个版本都留痕以供审计。它作为一道控制来支持你的披露流程——但不取代你的顾问或董事会对“披露什么”的判断。",
    },
    {
      question: "它支持哪些语言？",
      answer:
        "默认支持英文、马来文和中文，也可增加其他语言。它还会保存你的已批准术语表，因此受监管的术语与品牌用语每次都翻译一致。",
    },
  ],
};

export const corporateComms: AgentUseCase = {
  structure: {
    slug: "corporate-comms",
    status: "available",
    pattern: "evaluator-optimizer",
    route: "solutions/agent-use-cases/corporate-comms",
    heroImage: "/images/agent-use-cases/corporate-comms-hero.jpg",
    cardImage: "/images/agent-use-cases/corporate-comms-card.jpg",
    relatedBlogSlug: "ai-corporate-communications-consistency",
    relatedUseCases: ["knowledge-agent", "contract-review"],
    trackLocation: "agent_use_case_corporate_comms",
    valueScore: 4,
    complexityScore: 3,
  },
  content: { en, ms, zh },
};
